// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/askPunttUnread.test.ts
import { type ChatArtifact } from "../artifactTypes";

export function artifactSeenKey(kind: "canvas" | "review", id: string): string {
  return `${kind}:${id}`;
}

/** Whether a review artifact is ready to notify/open (not in limbo or pipeline). */
export function isReviewArtifactReady(artifact: ChatArtifact): boolean {
  if (artifact.kind !== "review") return true;
  return artifact.isGenerating !== true;
}

export function isProjectReviewItemReady(item: {
  ticketId?: string | null;
  status?: string | null;
}): boolean {
  if (!item.ticketId) return false;
  return item.status !== "in_review";
}

export function isArtifactUnread(
  artifact: ChatArtifact,
  unseenArtifactKeys: ReadonlySet<string> | readonly string[],
): boolean {
  if (!isReviewArtifactReady(artifact)) return false;
  if (artifact.isGenerating) return false;
  const unseen =
    unseenArtifactKeys instanceof Set
      ? unseenArtifactKeys
      : new Set(unseenArtifactKeys);
  if (artifact.kind === "review" && artifact.ticketId) {
    return unseen.has(artifactSeenKey("review", artifact.ticketId));
  }
  if (artifact.kind === "canvas") {
    return unseen.has(artifactSeenKey("canvas", artifact.messageId));
  }
  return false;
}

export function latestMessageId(
  messages: readonly { _id: string }[],
): string | undefined {
  if (messages.length === 0) return undefined;
  return messages[messages.length - 1]?._id;
}

export type ReadReceiptMessage = {
  _id: string;
  role: "user" | "assistant";
  status?: "pending" | "completed" | null;
  content?: string;
};

/** Whether the tail message is settled enough to advance the read cursor. */
export function isMessageEligibleForReadReceipt(
  message: ReadReceiptMessage,
  isReviewStatusMessage?: (message: ReadReceiptMessage) => boolean,
): boolean {
  if (message._id.startsWith("local-")) return false;
  if (message.role === "assistant") {
    if (message.status === "pending") return false;
    if (
      !message.content?.trim() &&
      message.status !== "completed" &&
      !isReviewStatusMessage?.(message)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Receipt key for mark-read — changes when the tail message id or completion
 * status changes so we re-mark after an assistant finishes (lastMessageAt
 * may bump on the server even though the message id is unchanged).
 */
export function tailMessageReadReceiptKey(
  messages: readonly ReadReceiptMessage[],
  isReviewStatusMessage?: (message: ReadReceiptMessage) => boolean,
): string | null {
  if (messages.length === 0) return null;
  const tail = messages[messages.length - 1];
  if (!isMessageEligibleForReadReceipt(tail, isReviewStatusMessage)) {
    return null;
  }
  return `${tail._id}:${tail.status ?? "completed"}`;
}
